import { useState } from 'react'

import { F0Button, F0ButtonProps } from '@factorialco/f0-react'
import PreviewWithExpenses from 'assets/images/finance/insights/banner-with-expenses.png'

import { AnnouncementBanner } from 'modules/core/components/AnnouncementBanner'
import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useEffect, useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import useLocalStorage from 'modules/core/lib/useLocalStorage'

import { useFinancialWorkspaceSeePayroll } from 'modules/finance/components/FinancialWorkspace/hooks/useFinancialWorkspaceSeePayroll'

import { UploadPayrollButton } from '../UploadPayroll'
import { UpsellExpensesButton } from '../UpsellExpenses'
import { useBannerNavigation } from './useBannerNavigation'

interface ModulesAvailability {
  expenses?: boolean
  payroll?: boolean
}

interface Button {
  title?: string
  type?: F0ButtonProps['variant']
  onClick?: () => void
  visible?: boolean
}

type ButtonType = 'payroll' | 'expenses'

interface BannerButtons {
  payroll?: Button
  expenses?: Button
}

interface Props {
  title: string
  description: string
  link?: {
    title: string
    src?: string | (() => string)
  }
  buttonsConfig?: BannerButtons
  localStorageKey: string
  source: string
  withButtons?: ButtonType[]
  modulesAvailability?: ModulesAvailability
  requestComponent?: string
  onFinishPayroll?: () => void
  onRestore?: () => void
  onClose?: () => void
}

export const LaunchBanner = ({
  title,
  description,
  link,
  buttonsConfig,
  localStorageKey,
  source,
  modulesAvailability,
  requestComponent,
  onFinishPayroll,
  onRestore,
  onClose,
}: Props) => {
  const [isHidden, setIsHidden] = useState(false)
  const [date, setDate] = useLocalStorage<Date>(`factorial:${localStorageKey}`)
  const { canSeeFinanceWorkspacePayroll, loading: loadingPayroll } =
    useFinancialWorkspaceSeePayroll()

  const hasExpenses = !!modulesAvailability?.expenses
  const hasPayroll = !!modulesAvailability?.payroll

  const { navigate } = useBannerNavigation({ link: link?.src, skipFetch: isHidden })

  const onClick = () => {
    track(trackingEvents.accounting.newFinancialWorkspace.bannerClosed, {
      source,
    })

    onClose?.()

    setDate(new Date())
  }

  const clickLink = () => {
    track(trackingEvents.accounting.newFinancialWorkspace.bannerLinkClicked, {
      source,
    })

    navigate()
  }

  const bannerViewedModules = useUnsafeCallback(() => {
    return [
      hasExpenses ? 'yesexpenses' : 'noexpenses',
      hasPayroll ? 'yespayroll' : 'nopayroll',
    ].join('_')
  }, [hasExpenses, hasPayroll])

  const trackBannerViewed = useUnsafeCallback(() => {
    track(trackingEvents.accounting.newFinancialWorkspace.bannerViewed, {
      source,
      modules: bannerViewedModules(),
    })
  }, [bannerViewedModules, source])

  useEffect(() => {
    const today = new Date()
    const storedDate = new Date(String(date))

    if (date && storedDate.getDate() != today.getDate()) {
      setDate(null)
      onRestore?.()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useUnsafeEffect(() => {
    if (loadingPayroll) return

    if (!!date || !canSeeFinanceWorkspacePayroll) {
      setIsHidden(true)
    } else {
      trackBannerViewed()
      setIsHidden(false)
    }
  }, [loadingPayroll, canSeeFinanceWorkspacePayroll, date, trackBannerViewed])

  return (
    <AnnouncementBanner
      image={PreviewWithExpenses}
      title={title}
      canBeClosed
      description={description}
      onClose={onClick}
      visible={!isHidden}
      variant={'base'}
      buttons={
        <>
          {buttonsConfig?.expenses?.visible && (
            <UpsellExpensesButton
              pageSource="payroll-management/policies"
              requestComponent={requestComponent}
            />
          )}
          {buttonsConfig?.payroll?.visible && (
            <UploadPayrollButton
              source={`${source}-fw-banner`}
              textButton={buttonsConfig?.payroll?.title}
              onFinish={onFinishPayroll}
              type={buttonsConfig?.payroll?.type}
            />
          )}
          {link && <F0Button onClick={clickLink} variant="outline" label={link.title} />}
        </>
      }
    />
  )
}
